<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Statement</h1></div>
        </div>
      </div>

      <!-- SUMMARY -->
      <div class="row mb-3">
        <div class="col-12 col-md-6 col-xl-3 mb-3">
          <div class="st-kpi">
            <span>Wallet Balance</span>
            <h2>{{ money(wallet.balance, currency) }}</h2>
            <small class="muted">{{ rangeLabel }}</small>
          </div>
        </div>
        <div class="col-12 col-md-6 col-xl-3 mb-3">
          <div class="st-kpi">
            <span>Money In</span>
            <h2 class="credit">+{{ money(summary.total_credit, currency) }}</h2>
            <small class="muted">{{ num(summary.count) }} entries in range</small>
          </div>
        </div>
        <div class="col-12 col-md-6 col-xl-3 mb-3">
          <div class="st-kpi">
            <span>Money Out</span>
            <h2 class="debit">&minus;{{ money(summary.total_debit, currency) }}</h2>
            <small class="muted">everything leaving your wallet</small>
          </div>
        </div>
        <div class="col-12 col-md-6 col-xl-3 mb-3">
          <div class="st-kpi">
            <span>Net Movement</span>
            <h2 :class="num(summary.net) < 0 ? 'debit' : 'credit'">
              {{ signed(summary.net, currency) }}
            </h2>
            <small class="muted">
              Opening {{ money(summary.opening_balance, currency) }}
              &rarr; Closing {{ money(summary.closing_balance, currency) }}
            </small>
          </div>
        </div>
      </div>

      <!-- FILTERS -->
      <div class="row align-items-end filter-row">
        <div class="col-12 col-md-3 col-lg-2 mb-2">
          <label>Period</label>
          <b-form-select v-model="filters.period" :options="periodOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2" v-if="filters.period === 'custom'">
          <label>From</label>
          <b-form-input v-model="filters.start" type="date"></b-form-input>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2" v-if="filters.period === 'custom'">
          <label>To</label>
          <b-form-input v-model="filters.end" type="date"></b-form-input>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Direction</label>
          <b-form-select v-model="filters.direction" :options="directionOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Type</label>
          <b-form-select v-model="filters.type" :options="typeOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Status</label>
          <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Min amount</label>
          <b-form-input v-model="filters.minAmount" type="number" min="0" step="0.01" placeholder="0.00"></b-form-input>
        </div>
        <div class="col-6 col-md-3 col-lg-2 mb-2">
          <label>Max amount</label>
          <b-form-input v-model="filters.maxAmount" type="number" min="0" step="0.01" placeholder="Any"></b-form-input>
        </div>
        <div class="col-12 col-md-3 col-lg-2 mb-2">
          <label>Code</label>
          <b-form-input v-model="filters.code" type="search" placeholder="e.g. ae112"
            @keyup.enter="applyFilters"></b-form-input>
        </div>
        <div class="col-12 col-md-4 col-lg-3 mb-2">
          <label>Search</label>
          <b-form-input v-model="filters.search" type="search"
            placeholder="code, remarks, title or email" @keyup.enter="applyFilters"></b-form-input>
        </div>
        <div class="col-6 col-md-2 col-lg-1 mb-2">
          <b-button variant="primary" block @click="applyFilters">Apply</b-button>
        </div>
        <div class="col-6 col-md-2 col-lg-1 mb-2">
          <b-button variant="light" block @click="resetFilters">Reset</b-button>
        </div>
        <div class="col-12 col-md-4 col-lg-2 mb-2 text-md-right">
          <a href="#" class="download-link" @click.prevent="exportAs('csv')">
            <span class="material-icons">download</span>
            <span>{{ exporting === 'csv' ? 'Preparing...' : 'CSV' }}</span>
          </a>
          <a href="#" class="download-link ml-3" @click.prevent="exportAs('pdf')">
            <span class="material-icons">picture_as_pdf</span>
            <span>{{ exporting === 'pdf' ? 'Preparing...' : 'PDF' }}</span>
          </a>
        </div>
      </div>

      <!-- LEDGER -->
      <div class="row">
        <div class="col-12">
          <div class="row m-0 mb-2" v-if="totalItems">
            <small class="text-muted">
              Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalItems }} entries
              &middot; all amounts in {{ currency }} &middot; newest first
            </small>
          </div>

          <b-table
            hover
            responsive
            striped
            bordered
            outlined
            show-empty
            :items="rows"
            :fields="fields"
            :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center py-4"><b-spinner label="Loading..."></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center py-3">No statement entries found.</div>
            </template>

            <template #cell(date)="data">{{ formatDate(data.item.date) }}</template>

            <template #cell(title)="data">
              <div class="st-desc">{{ data.item.title || typeLabel(data.item.type) }}</div>
              <small class="text-muted d-block" v-if="data.item.remarks">{{ data.item.remarks }}</small>
              <small class="text-muted d-block" v-if="partyName(data.item.counterparty)">
                {{ isDebit(data.item) ? 'To' : 'From' }} {{ partyName(data.item.counterparty) }}
              </small>
              <small class="text-muted d-block st-ref">Ref {{ data.item.code || data.item.reference }}</small>
            </template>

            <template #cell(type)="data">
              <span class="type-pill" :class="'t-' + (data.item.type || 'other')">
                {{ typeLabel(data.item.type) }}
              </span>
            </template>

            <template #cell(status)="data">
              <span class="status-pill" :class="statusClass(data.item.status)">
                {{ statusLabel(data.item.status) }}
              </span>
            </template>

            <template #cell(debit)="data">
              <span v-if="isDebit(data.item)" class="debit amount-cell">{{ amountOf(data.item).toFixed(2) }}</span>
              <span v-else class="muted-dash">&mdash;</span>
            </template>

            <!-- No fee amount or rate in the LIST: the customer is charged the full
                 amount, so a fee against a row implies they paid something extra.
                 The breakdown is still available in the detail view. -->
            <template #cell(credit)="data">
              <span v-if="!isDebit(data.item)" class="credit amount-cell">{{ amountOf(data.item).toFixed(2) }}</span>
              <span v-else class="muted-dash">&mdash;</span>
            </template>

            <template #cell(balance_after)="data">
              <span class="amount-cell" :class="num(data.item.balance_after) < 0 ? 'debit' : ''">
                {{ num(data.item.balance_after).toFixed(2) }}
              </span>
            </template>

            <template #cell(details)="data">
              <a href="#" class="btn btn-primary btn-sm" @click.prevent="openDetail(data.item)">Detail</a>
            </template>

            <!-- Opening balance only on page 1: on later pages it would be the balance
                 before a different entry, which would simply be wrong. -->
            <template #top-row v-if="currentPage === 1 && rows.length">
              <td colspan="4" class="st-boundary">Opening Balance</td>
              <td class="text-right st-boundary">&mdash;</td>
              <td class="text-right st-boundary">&mdash;</td>
              <td class="text-right st-boundary amount-cell">{{ num(summary.opening_balance).toFixed(2) }}</td>
              <td class="st-boundary"></td>
            </template>

            <!-- Totals for the whole filtered range, from the API summary. -->
            <template #bottom-row v-if="rows.length">
              <td colspan="4" class="st-boundary">
                Totals for {{ rangeLabel }}
                <small class="text-muted d-block">{{ num(summary.count) }} entries in range</small>
              </td>
              <td class="text-right st-boundary amount-cell debit">{{ num(summary.total_debit).toFixed(2) }}</td>
              <td class="text-right st-boundary amount-cell credit">{{ num(summary.total_credit).toFixed(2) }}</td>
              <td class="text-right st-boundary amount-cell">
                <strong>{{ num(summary.closing_balance).toFixed(2) }}</strong>
                <small class="text-muted d-block">closing</small>
              </td>
              <td class="st-boundary"></td>
            </template>
          </b-table>

          <div class="st-pager" v-if="rows.length">
            <div class="st-pagesize">
              <span>Rows per page</span>
              <b-form-select v-model="perPage" :options="pageSizeOptions" size="sm"></b-form-select>
            </div>
            <nav aria-label="Statement pagination">
              <ul class="pagination mb-0">
                <li class="page-item" :class="currentPage <= 1 ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">&laquo; Previous</a>
                </li>
                <li class="page-item" v-for="index in pageWindow" :key="index"
                    :class="currentPage === index ? 'active' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(index)">{{ index }}</a>
                </li>
                <li class="page-item" :class="currentPage >= totalPages ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
                </li>
              </ul>
            </nav>
            <div class="st-pageinfo">Page {{ currentPage }} of {{ totalPages }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- DETAIL -->
    <b-modal id="statement-detail-modal" centered size="lg" title="Statement Entry">
      <div v-if="detailLoading" class="text-center py-5">
        <b-spinner label="Loading..."></b-spinner>
      </div>
      <div v-else class="txn-detail">
        <div class="txn-head">
          <div class="txn-amount" :class="detailIsDebit ? 'debit' : 'credit'">
            {{ signed(detailSigned, currency) }}
          </div>
          <span class="status-pill" :class="statusClass(detail.status)">
            {{ statusLabel(detail.status) }}
          </span>
          <div class="txn-sub" v-if="detail.title">{{ detail.title }}</div>
        </div>

        <div class="txn-section" v-for="(section, si) in detailSections" :key="'s' + si">
          <h6 class="txn-section-title">{{ section.title }}</h6>
          <div class="txn-row" v-for="(f, i) in section.items" :key="'r' + si + '-' + i">
            <span class="txn-key">{{ f.label }}</span>
            <span class="txn-val">{{ f.value || '-' }}</span>
          </div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="secondary" @click="cancel()">Close</b-button>
      </template>
    </b-modal>

    <b-toast id="statement-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ message }}</p></div>
    </b-toast>
    <b-toast id="statement-success-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Export</strong>
      </div>
      <div><p>Your download has started.</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";
import { cleanText } from "@/utils/format";

/** Fallback only - the API describes its own options in filters.available. */
const FALLBACK_FILTERS = {
  direction: ["credit", "debit"],
  type: ["load", "withdrawal", "transfer", "merchant_payment", "donation", "fee", "other"],
  status: ["completed", "pending", "failed"],
  period: ["today", "7d", "30d", "90d", "month", "year", "all"],
};

const PERIOD_LABELS = {
  today: "Today", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days",
  month: "This month", year: "This year", all: "All time",
};

export default {
  name: "UserStatement",
  components: { "app-header": AppHeader },
  data() {
    return {
      isLoading: false,
      detailLoading: false,
      exporting: "",
      perPage: 20,
      pageSizeOptions: [
        { value: 20, text: "20" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],
      filters: {
        period: "all", direction: "", type: "", status: "",
        start: "", end: "", search: "", code: "", minAmount: "", maxAmount: "",
      },
      fields: [
        { key: "date", label: "Date", sortable: true, class: "col-date" },
        { key: "title", label: "Description", class: "col-desc" },
        { key: "type", label: "Type", sortable: true, class: "col-type" },
        { key: "status", label: "Status", class: "col-status" },
        { key: "debit", label: "Debit (−)", class: "col-money text-right" },
        { key: "credit", label: "Credit (+)", class: "col-money text-right" },
        { key: "balance_after", label: "Balance", class: "col-money text-right" },
        { key: "details", label: "" },
      ],
    };
  },
  computed: {
    ...mapState(["userStatement", "userStatementDetail", "message"]),
    statement: function () {
      const s = this.userStatement;
      return (s && typeof s === "object") ? s : {};
    },
    wallet: function () { return this.statement.wallet || {}; },
    summary: function () { return this.statement.summary || {}; },
    pagination: function () { return this.statement.pagination || {}; },
    rows: function () { return Array.isArray(this.statement.results) ? this.statement.results : []; },
    currency: function () { return this.wallet.currency || "USD"; },
    detail: function () { return this.userStatementDetail || {}; },
    detailIsDebit: function () { return this.isDebit(this.detail); },
    detailSigned: function () { return this.signedOf(this.detail); },
    rangeLabel: function () {
      const r = this.statement.range || {};
      if (r.start && r.end) { return r.start + " to " + r.end; }
      return PERIOD_LABELS[r.label] || PERIOD_LABELS[this.filters.period] || "All time";
    },

    available: function () {
      const f = this.statement.filters || {};
      return f.available || FALLBACK_FILTERS;
    },
    periodOptions: function () {
      const list = this.available.period || FALLBACK_FILTERS.period;
      const out = list.map(function (v) { return { value: v, text: PERIOD_LABELS[v] || v }; });
      out.push({ value: "custom", text: "Custom range..." });
      return out;
    },
    directionOptions: function () {
      return [{ value: "", text: "All" }].concat(
        (this.available.direction || []).map(function (v) {
          return { value: v, text: v.charAt(0).toUpperCase() + v.slice(1) };
        })
      );
    },
    typeOptions: function () {
      const vm = this;
      return [{ value: "", text: "All" }].concat(
        (this.available.type || []).map(function (v) { return { value: v, text: vm.typeLabel(v) }; })
      );
    },
    statusOptions: function () {
      const vm = this;
      return [{ value: "", text: "All" }].concat(
        (this.available.status || []).map(function (v) { return { value: v, text: vm.statusLabel(v) }; })
      );
    },

    totalItems: function () { return this.pagination.count || 0; },
    offset: function () { return this.pagination.offset || 0; },
    currentPage: function () { return Math.floor(this.offset / this.perPage) + 1; },
    totalPages: function () { return Math.max(1, Math.ceil(this.totalItems / this.perPage)); },
    firstIndex: function () { return this.totalItems ? this.offset + 1 : 0; },
    lastIndex: function () { return Math.min(this.offset + this.rows.length, this.totalItems); },
    pageWindow: function () {
      const total = this.totalPages, cur = this.currentPage;
      let start = Math.max(1, cur - 2);
      const end = Math.min(total, start + 4);
      start = Math.max(1, Math.min(start, end - 4));
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },

    detailSections: function () {
      const d = this.detail;
      if (!d || !Object.keys(d).length) { return []; }
      const cp = d.counterparty || {};
      const sections = [
        {
          title: "Entry",
          items: [
            { label: "Reference", value: d.code || d.reference },
            { label: "Type", value: this.typeLabel(d.type) },
            { label: "Direction", value: this.isDebit(d) ? "Debit (money out)" : "Credit (money in)" },
            { label: "Status", value: this.statusLabel(d.status) },
            // Only the detail payload carries this - it is not in the list.
            { label: "Transaction Status", value: d.transaction_status },
            { label: "Date", value: this.formatDate(d.date) },
          ],
        },
        {
          // The fee RATE is not shown anywhere - it varies per merchant and per
          // transaction. The fee amount and net stay in the detail view; only the
          // LIST omits them, so the ledger reads as what the customer was charged.
          title: "Amount",
          items: [
            { label: "Amount", value: this.money(d.amount, this.currency) },
            { label: "Fee", value: this.money(d.fee, this.currency) },
            { label: "Net Amount", value: this.money(d.net_amount, this.currency) },
            { label: "Balance After", value: this.money(d.balance_after, this.currency) },
          ],
        },
        {
          title: "Counterparty",
          items: [
            { label: "Name", value: this.partyName(cp) },
            { label: "Email", value: cp.email },
          ],
        },
        {
          title: "Notes",
          items: [{ label: "Remarks", value: cleanText(d.remarks || "") }],
        },
      ];
      // Donation fields only exist on donation entries.
      if (d.campaign_id || d.donor_id) {
        sections.push({
          title: "Donation",
          items: [
            { label: "Campaign ID", value: d.campaign_id },
            { label: "Donor ID", value: d.donor_id },
          ],
        });
      }
      return sections;
    },
  },
  created() {
    this.syncFromRoute();
    this.fetchList();
  },
  watch: {
    "$route.query": function () { this.syncFromRoute(); this.fetchList(); },
    perPage: function () {
      if (this.currentPageFromRoute() > 1) { this.pushQuery(1); } else { this.fetchList(); }
    },
  },
  methods: {
    ...mapActions(["listUserStatement", "getUserStatementDetail", "exportUserStatement"]),

    num(v) { const n = Number(v); return isNaN(n) ? 0 : n; },
    money(value, currency) { return (currency ? currency + " " : "") + this.num(value).toFixed(2); },
    signed(value, currency) {
      const n = this.num(value);
      return (n < 0 ? "−" : "+") + (currency ? currency + " " : "") + Math.abs(n).toFixed(2);
    },
    /** `direction` is authoritative; the sign of signed_amount is the fallback. */
    isDebit(row) {
      if (!row) { return false; }
      if (row.direction) { return String(row.direction).toLowerCase() === "debit"; }
      return this.num(row.signed_amount) < 0;
    },
    signedOf(row) {
      if (!row) { return 0; }
      if (row.signed_amount !== undefined && row.signed_amount !== null) { return this.num(row.signed_amount); }
      const a = Math.abs(this.num(row.amount));
      return this.isDebit(row) ? -a : a;
    },
    /** Magnitude only - the column it lands in carries the sign. */
    amountOf(row) {
      if (!row) { return 0; }
      const a = (row.amount !== undefined && row.amount !== null)
        ? this.num(row.amount) : Math.abs(this.num(row.signed_amount));
      return Math.abs(a);
    },
    /** Names arrive doubled-spaced ("admin  user") and can contain a literal "null". */
    partyName(party) { return party ? cleanText(party.name || "") : ""; },
    typeLabel(type) {
      if (!type) { return "Other"; }
      return String(type).replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    },
    statusLabel(status) {
      if (!status) { return "-"; }
      return String(status).charAt(0).toUpperCase() + String(status).slice(1);
    },
    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (["completed", "paid", "success"].indexOf(s) !== -1) { return "is-success"; }
      if (["pending", "processing", "initiated"].indexOf(s) !== -1) { return "is-pending"; }
      return "is-failed";
    },
    formatDate(value) {
      if (!value) { return "-"; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },

    currentPageFromRoute() {
      const p = parseInt(this.$route.query.page, 10);
      return (!p || p < 1) ? 1 : p;
    },
    currentQuery(extra) {
      const f = this.filters;
      const custom = f.period === "custom";
      return Object.assign({
        period: custom ? "" : f.period,
        start: custom ? f.start : "",
        end: custom ? f.end : "",
        direction: f.direction,
        type: f.type,
        status: f.status,
        code: f.code,
        search: f.search,
        minAmount: f.minAmount,
        maxAmount: f.maxAmount,
        limit: this.perPage,
        offset: (this.currentPageFromRoute() - 1) * this.perPage,
      }, extra || {});
    },
    syncFromRoute() {
      const q = this.$route.query || {};
      this.filters.period = q.period || ((q.start || q.end) ? "custom" : "all");
      this.filters.direction = q.direction || "";
      this.filters.type = q.type || "";
      this.filters.status = q.status || "";
      this.filters.start = q.start || "";
      this.filters.end = q.end || "";
      this.filters.search = q.search || "";
      this.filters.code = q.code || "";
      this.filters.minAmount = q.min_amount || "";
      this.filters.maxAmount = q.max_amount || "";
    },
    fetchList() {
      const vm = this;
      this.isLoading = true;
      return this.listUserStatement(this.currentQuery())
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    pushQuery(page) {
      const f = this.filters;
      const q = {};
      if (page && page > 1) { q.page = page; }
      if (f.period && f.period !== "all" && f.period !== "custom") { q.period = f.period; }
      if (f.period === "custom") {
        if (f.start) { q.start = f.start; }
        if (f.end) { q.end = f.end; }
      }
      if (f.direction) { q.direction = f.direction; }
      if (f.type) { q.type = f.type; }
      if (f.status) { q.status = f.status; }
      if (f.code) { q.code = f.code; }
      if (f.search) { q.search = f.search; }
      if (f.minAmount !== "") { q.min_amount = f.minAmount; }
      if (f.maxAmount !== "") { q.max_amount = f.maxAmount; }
      if (JSON.stringify(q) === JSON.stringify(this.$route.query || {})) { this.fetchList(); return; }
      this.$router.push({ query: q }).catch(function () {});
    },
    applyFilters() { this.pushQuery(1); },
    resetFilters() {
      this.filters = {
        period: "all", direction: "", type: "", status: "",
        start: "", end: "", search: "", code: "", minAmount: "", maxAmount: "",
      };
      this.pushQuery(1);
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) { return; }
      this.pushQuery(page);
    },

    openDetail(row) {
      const id = row && (row.id || row.code || row.reference);
      if (!id) { return; }
      this.detailLoading = true;
      const vm = this;
      this.$bvModal.show("statement-detail-modal");
      return this.getUserStatementDetail(id)
        .then(function (data) {
          vm.detailLoading = false;
          if (!data) { vm.$bvToast.show("statement-error-toast"); }
        })
        .catch(function () {
          vm.detailLoading = false;
          vm.$bvToast.show("statement-error-toast");
        });
    },

    /**
     * The SERVER builds the file. Sending the current filters means the download
     * matches what is on screen; rebuilding it here would risk a different document.
     */
    exportAs(format) {
      if (this.exporting) { return; }
      this.exporting = format;
      const vm = this;
      return this.exportUserStatement(this.currentQuery({ format: format }))
        .then(function (res) {
          vm.exporting = "";
          vm.$bvToast.show(res && res.ok ? "statement-success-toast" : "statement-error-toast");
        })
        .catch(function () {
          vm.exporting = "";
          vm.$bvToast.show("statement-error-toast");
        });
    },
  },
};
</script>

<style scoped>
.st-kpi {
  background: #fff;
  border: 1px solid #eceff4;
  border-radius: 8px;
  padding: 16px 18px;
  height: 100%;
}
.st-kpi span { color: #8a94a6; font-size: 13px; }
.st-kpi h2 { margin: 6px 0 2px; font-size: 22px; }
.st-kpi .muted { color: #8a94a6; font-size: 12px; }
.credit { color: #1e8c50; }
.debit { color: #c83c3c; }
.amount-cell { font-variant-numeric: tabular-nums; white-space: nowrap; }
.muted-dash { color: #cbd2dc; }
.st-desc { font-weight: 500; }
.st-ref { font-size: 11px; opacity: .8; }
.st-boundary { background: #f7f9fc; font-weight: 600; border-top: 2px solid #e4e9f0 !important; }
.filter-row label { font-size: 12px; color: #8a94a6; margin-bottom: 2px; }
.download-link { text-decoration: none; color: #0066b3; display: inline-flex; align-items: center; }
.download-link .material-icons { font-size: 18px; margin-right: 4px; }
.status-pill, .type-pill {
  display: inline-block; padding: 2px 10px; border-radius: 12px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.status-pill.is-success { background: #e7f6ed; color: #1e8c50; }
.status-pill.is-pending { background: #fff5e0; color: #9a6b00; }
.status-pill.is-failed  { background: #fdeaea; color: #c83c3c; }
.type-pill { background: #eef2f7; color: #4a5568; font-weight: 500; }
.type-pill.t-merchant_payment { background: #e7f0fb; color: #0066b3; }
.type-pill.t-fee { background: #fdeaea; color: #c83c3c; }
.type-pill.t-withdrawal, .type-pill.t-transfer { background: #f4eefb; color: #6b46a8; }
.type-pill.t-load { background: #e7f6ed; color: #1e8c50; }
.st-pager { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; margin-top: 6px; }
.st-pagesize { display: flex; align-items: center; font-size: 13px; color: #6b7280; }
.st-pagesize span { margin-right: 8px; white-space: nowrap; }
.st-pagesize select { width: auto !important; min-width: 76px; }
.st-pageinfo { font-size: 13px; color: #6b7280; }
.txn-head { border: 1px solid #e9ecef; background: #f8f9fb; border-radius: 8px; padding: 16px 18px; margin-bottom: 18px; }
.txn-amount { font-size: 26px; font-weight: 700; line-height: 1.2; display: inline-block; margin-right: 10px; }
.txn-sub { color: #6b7280; font-size: 13px; margin-top: 6px; }
.txn-section { margin-bottom: 16px; }
.txn-section-title { font-size: 12px; text-transform: uppercase; letter-spacing: .4px; color: #8a94a6; margin-bottom: 8px; }
.txn-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f2f4f7; font-size: 14px; }
.txn-row:last-child { border-bottom: 0; }
.txn-key { color: #6b7280; margin-right: 16px; }
.txn-val { font-weight: 500; text-align: right; word-break: break-word; }
</style>
